import { Cart } from '../models/Cart';
import { IConnector, OnCartUpdatedHandler } from './IConnector';
import { CartLine } from '../models/CartLine';
declare const Shopify: {
    routes: {
        root: string;
    };
};

let onCartUpdatedHandler: OnCartUpdatedHandler | undefined;
export const shopifyConnector: IConnector = {
    async getCurrentCart(): Promise<Cart> {
        const cartState: CartState = await fetch('/cart.js').then(r =>
            r.json()
        );
        return {
            note: cartState.note || '',
            originalTotalPrice: cartState.original_total_price / 100,
            token: cartState.token,
            totalDiscount: cartState.total_discount / 100,
            totalPrice: cartState.total_price / 100,
            totalQuantity: cartState.item_count,
            currency: cartState.currency,
            lines: cartState.items.map<CartLine>(i => ({
                key: i.key,
                productId: `${i.product_id}`,
                productName: i.product_title,
                quantity: i.quantity,
                selectedOptions: i.options_with_values,
                url: i.url,
                variantId: `${i.variant_id}`,
                variantName: i.variant_title || '',
                requiresShipping: i.requires_shipping,
                finalLinePrice: i.final_line_price / 100,
                finalPrice: i.final_price / 100,
                imageUrl: i.image,
                originalPrice: i.original_price / 100,
                originalLinePrice: i.original_line_price / 100,
                productHasOnlyDefaultVariant:
                    i.product_has_only_default_variant,
                properties: i.properties ?? {},
                discounts: (i.line_level_discount_allocations ?? []).map(
                    llda => ({
                        amount: llda.amount / 100,
                        title: llda.discount_application.title
                    })
                )
            }))
        };
    },
    async addProductVariantToCart(data: {
        variantId: string;
        quantity: number;
    }): Promise<void> {
        const { variantId, quantity = 1 } = data;
        await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                items: [
                    {
                        id: variantId,
                        properties: {},
                        quantity
                    }
                ]
            })
        });
        // Trigger a cart update event that many themes can listen to.
        // This is not a standard Shopify event, but many themes use it.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
    },
    onCartUpdated(handler) {
        onCartUpdatedHandler = handler;
    },
    goToCart() {
        window.location.href = Shopify.routes.root + 'cart';
    },
    getScaledImage(imageUrl: string, width: number): string {
        const url = new URL(imageUrl);
        if (url.protocol === 'data:') {
            return imageUrl;
        }
        const dpiWidth = Math.round(width * (window.devicePixelRatio || 1));
        url.searchParams.set('width', dpiWidth.toString());
        return url.toString();
    }
};

// Spy ajax calls to update cart URLs (fetch and XMLHttpRequest)
const cartEndingUrls = [
    `cart/add.js`,
    `cart/update.js`,
    `cart/change.js`,
    `cart/clear.js`,
    `cart/add`,
    `cart/update`,
    `cart/change`,
    `cart/clear`
];

// Spy fetch function
if (typeof window.fetch === 'function') {
    const fetch = window.fetch;
    window.fetch = function (...args) {
        const response = fetch.apply(window, args);
        const startingUrl = window.location.origin + Shopify.routes.root;

        response
            .then(res => {
                if (
                    cartEndingUrls
                        .map(endingUrl => `${startingUrl}${endingUrl}`)
                        .includes(res.url)
                ) {
                    res.clone()
                        .json()
                        .then(() => onCartUpdatedHandler?.())
                        .catch(() => {});
                }
            })
            .catch(() => {});

        return response;
    };
}

// Spy XMLHttpRequest calls
const open = window.XMLHttpRequest.prototype.open;
const openReplacement: typeof open = function openReplacement(
    this: XMLHttpRequest
) {
    this.addEventListener('load', function () {
        const fullStartingUrl = window.location.origin + Shopify.routes.root;
        const relativeStartingUrl = Shopify.routes.root;
        if (
            cartEndingUrls
                .flatMap(endingUrl => [
                    `${fullStartingUrl}${endingUrl}`,
                    `${relativeStartingUrl}${endingUrl}`
                ])
                .includes(this.responseURL)
        ) {
            onCartUpdatedHandler && onCartUpdatedHandler();
        }
    });
    // eslint-disable-next-line prefer-rest-params
    return open.apply(this, arguments as unknown as Parameters<typeof open>);
};

window.XMLHttpRequest.prototype.open = openReplacement;

type CartState = {
    token: string;
    note: string | null;
    attributes: { [index: string]: string };
    total_price: number;
    total_weight: number;
    item_count: number;
    items: CartLineItem[];
    requires_shipping: boolean;
    currency: string;
    items_subtotal_price: number;
    original_total_price: number;
    total_discount: number;
    cart_level_discount_applications: {
        type: string;
        key: string;
        title: string;
        description: string;
        value: string;
        created_at: string;
        value_type: string;
        allocation_method: string;
        target_selection: string;
        target_type: string;
        total_allocated_amount: number;
    }[];
};

type CartLineItem = {
    id: number;
    quantity: number;
    variant_id: number;
    key: string;
    title: string;
    price: number;
    line_price: number;
    final_price: number;
    final_line_price: number;
    original_price: number;
    original_line_price: number;
    sku: string;
    grams: number;
    vendor: string;
    taxable: boolean;
    product_id: number;
    product_has_only_default_variant: boolean;
    properties: {
        [index: string]: string;
    };
    gift_card: boolean;
    url: string;
    featured_image: {
        url: string;
        aspect_ratio: number;
        alt: string;
    };
    image: string;
    handle: string;
    requires_shipping: boolean;
    product_title: string;
    product_description: string;
    product_type: string;
    variant_title: string | null;
    variant_options: string[];
    options_with_values: {
        name: string;
        value: string;
    }[];
    line_level_discount_allocations?: {
        amount: number;
        discount_application: {
            type: string;
            key: string;
            title: string;
            description: null;
            value: string;
            created_at: string;
            value_type: string;
            allocation_method: string;
            target_selection: string;
            target_type: string;
            total_allocated_amount: number;
        };
    }[];
};
